'use client';

import React, { FC, useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command';
import { Popover, PopoverTrigger } from '@/components/ui/popover';
import { writeLanguageCookie, writeLanguageExplicitCookie } from '@/i18n/languageCookies';
import { ELocalStorageKeys } from '@/lib/enums';
import { cn } from '@/lib/utils';
import { useAppState } from '@/state/hooks/app.state.hook';
import { webStorageUtil } from '@/state/webStorage.state.service';
import { setLanguageExplicitlySelected } from '@web-store-platforms/locales';

import { Check, ChevronsUpDown } from 'lucide-react';

import { StyledFooterLanguageMenu, useFooterScopeProps } from './footerStyle';

interface LanguagePickerProps {
  className?: string;
}

export const LanguagePicker: FC<LanguagePickerProps> = ({ className }) => {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const { languagesList, currentLanguage, setCurrentLanguage, setIsGlobalLoading } = useAppState();
  const footerScopeProps = useFooterScopeProps();
  const inputRef = useRef<HTMLInputElement>(null);

  const filteredLanguages = useMemo(() => {
    if (!searchValue || !languagesList) return languagesList || [];

    return languagesList.filter((item) => {
      const search = searchValue.toLowerCase();

      return (
        item.language.toLowerCase().includes(search) || item.isoCode.toLowerCase().includes(search)
      );
    });
  }, [searchValue, languagesList]);

  const handleLanguageChange = async (language: string) => {
    if (language === currentLanguage) return;

    setIsGlobalLoading(true);

    setCurrentLanguage(language);
    webStorageUtil.set(ELocalStorageKeys.CURRENT_LANGUAGE, language);
    setLanguageExplicitlySelected();
    writeLanguageExplicitCookie();
    writeLanguageCookie(language);

    setOpen(false);
    setSearchValue('');
  };

  useEffect(() => {
    if (!open) {
      setSearchValue('');
    }
  }, [open]);

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [open]);

  const currentLanguageData = languagesList?.find((lang) => lang.isoCode === currentLanguage);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {/* No variant: the outline hover utilities tie with footer CSS and would win on hover. */}
        <Button
          variant={null}
          role="combobox"
          aria-expanded={open}
          aria-label="Select language"
          className={cn(
            'footer-language-picker h-10 w-[238px] justify-between border border-current/20 bg-background text-sm transition-transform duration-150 hover:opacity-90',
            className
          )}
        >
          <div className="flex items-center gap-2">
            <span>{currentLanguageData?.language || 'Select Language'}</span>
          </div>
          <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <StyledFooterLanguageMenu
        {...footerScopeProps}
        data-testid="PortalFooter-Container"
        className="footer-language-menu border-current/20 w-[238px] rounded-md border p-0 text-sm shadow-md"
        align="end"
      >
        <Command className="[&_[cmdk-input-wrapper]]:border-current/20 bg-transparent text-inherit">
          <CommandInput
            ref={inputRef}
            placeholder="Search language"
            value={searchValue}
            onValueChange={setSearchValue}
            className="border-0 bg-transparent text-inherit placeholder:opacity-50 focus:ring-0"
          />
          <CommandList className="max-h-[300px]">
            <CommandEmpty className="text-inherit">No language found.</CommandEmpty>
            <CommandGroup>
              {filteredLanguages.map((item) => (
                <CommandItem
                  key={item.language}
                  value={item.isoCode}
                  onSelect={() => handleLanguageChange(item.isoCode)}
                  className="data-[selected=true]:bg-current/10 hover:bg-current/10 flex cursor-pointer items-center gap-3 px-3 py-2 text-inherit transition-colors data-[selected=true]:text-inherit"
                >
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{item.language}</span>
                  </div>
                  {item.isoCode === currentLanguage && <Check className="ml-auto size-4" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </StyledFooterLanguageMenu>
    </Popover>
  );
};
